import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Mark, { Range } from "mark.js";
import get from "lodash/get";

import { getIsPlaying, getStartingPosition } from "../../../store/modules/audio/selectors";
import { jumpToWord } from "../../../store/modules/audio/actions";

import { ClassName, optionCollection } from "./settings";
import { RecordTextWithMarks } from "./RecordTextWithMarks";

interface IProps {
  text: string;
}

export const HighlightRange: React.FC<IProps> = ({ text }) => {
  const textRef = React.useRef<HTMLElement>(null);
  const markInstance = React.useRef<Mark | null>(null);
  const dispatch = useDispatch();
  const startingPosition = useSelector(getStartingPosition);
  const isPlaying = useSelector(getIsPlaying);

  const handleJumpToWord = useCallback(() => {
    const { current } = markInstance;

    if (current) {
      current.unmark(optionCollection[ClassName.audio]);
      const selection = window.getSelection();
      const anchorOffset = get(selection, "anchorOffset");

      if (isPlaying && anchorOffset) {
        dispatch(jumpToWord(anchorOffset));
        selection!.removeAllRanges();
      }
    }
  }, [isPlaying, dispatch]);
  /* initialize mark ref */
  useEffect(() => {
    const { current } = textRef;

    if (current) {
      markInstance.current = new Mark(current);
      markInstance.current.unmark();
    }
  }, []);
  /* remove all marks */
  useEffect(() => {
    const { current } = markInstance;

    if (current) {
      current.unmark();
    }
  }, [text]);
  /* create new mark range */
  useEffect(() => {
    const { current } = markInstance;
    if (current) {
      const { startPosition, endPosition } = startingPosition;
      const range: Range = {
        start: startPosition,
        length: endPosition - startPosition,
      };
      current.unmark(optionCollection[ClassName.audio]);
      current.markRanges([range], optionCollection[ClassName.audio]);
    }
  }, [startingPosition]);
  /* unmark .audio */
  useEffect(() => {
    const { current } = markInstance;
    if (current && !isPlaying) {
      current.unmark(optionCollection[ClassName.audio]);
    }
  }, [isPlaying]);

  return (
    <RecordTextWithMarks
      ref={textRef}
      onClick={handleJumpToWord}
      css={`position: absolute; top: 0; right: 0; bottom: 0; left: 0; z-index: ${isPlaying ? 1 : -1}`}
    >
      {text}
    </RecordTextWithMarks>
  );
};
